import React from "react";
import { ShareNetwork, DownloadSimple, LinkSimple, WhatsappLogo } from "@phosphor-icons/react";
import ActiveSharesList from "@/components/social/ActiveSharesList";
import CaptionGenerator from "@/components/social/CaptionGenerator";

/**
 * SocialActions — bottoni di azione sotto l'anteprima (Condividi / Scarica /
 * WhatsApp / Crea link pubblico) + footer info + CaptionGenerator.
 *
 * Estratto da SocialStudioTab.jsx (iter 274) per ridurre il file principale.
 * Stateless: tutti gli handler vengono dal parent.
 */
export default function SocialActions({
  active,
  isStatic,
  isCarousel,
  exporting,
  busy,
  previewUrl,
  carouselSlides,
  shares,
  carouselIdx,
  onShare,
  onStaticShare,
  onDownload,
  onStaticDownload,
  onWhatsAppShare,
  onCreatePublicLink,
  onDownloadSlide,
  onDownloadAsReel,
  onCopyShareUrl,
  onRevokeShare,
  propertyId,
  // iter v27 r11 — quando true il bottone "Scarica PDF" della brochure diventa
  // verde "Salva su File · Condividi" perché il PDF è già pronto in memoria
  // (pattern 2-step iOS per il gesture user fresco).
  brochurePdfReady,
  // iter v28 r14 — occasione + dati evento per CaptionGenerator (open_house / venduto)
  captionOccasion = "nuovo_annuncio",
  captionEventDate,
  captionEventTime,
  captionEventTimeEnd,
  captionDaysToSell,
  // iter v28 r38 — single source of truth per il tono del testo
  captionTone,
  setCaptionTone,
  // iter v28 r146 — User M1063: mostra/nascondi CaptionGenerator dentro
  // questo pannello (default false ora: il caption è renderizzato a SX).
  showCaption = false,
}) {
  const noPreview = isCarousel ? carouselSlides.length === 0 : !previewUrl;
  const isVideoFormat = active === "reel" || active === "before_after";
  const isBrochureReady = active === "brochure" && !!brochurePdfReady;

  return (
    <>
      {/* Actions */}
      <div className="grid grid-cols-2 gap-3" data-testid="social-actions">
        <button
          onClick={isStatic ? onStaticShare : onShare}
          disabled={isStatic ? exporting : busy || noPreview}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#7C8B6E] hover:bg-[#90A082] text-white text-[13px] font-semibold transition-colors disabled:opacity-50"
          data-testid="social-share-btn"
        >
          <ShareNetwork size={15} weight="fill" /> {exporting && isStatic ? "Genero…" : "Condividi"}
        </button>
        <button
          onClick={isStatic ? onStaticDownload : onDownload}
          disabled={isStatic ? exporting : busy || noPreview}
          className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-[13px] font-semibold transition-colors disabled:opacity-50 ${
            isBrochureReady
              ? "bg-[#7C8B6E] text-white hover:bg-[#6A7860]"
              : "bg-white border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#F1F4ED]"
          }`}
          data-testid="social-download-btn"
        >
          <DownloadSimple size={15} weight="bold" />{" "}
          {isCarousel
            ? "Scarica .zip"
            : isStatic && active === "brochure"
            ? exporting
              ? "Genero PDF…"
              : isBrochureReady
                ? "Salva su File · Condividi"
                : "Scarica PDF"
            : isStatic
            ? exporting
              ? "Genero…"
              : "Scarica PNG"
            : "Scarica"}
        </button>
      </div>
      {isBrochureReady && (
        <p className="text-[10.5px] text-[#7C8B6E] text-center leading-snug font-medium px-1 mt-1.5" data-testid="brochure-ready-hint">
          PDF stampabile pronta. Tocca per salvarla nei File o condividerla.
        </p>
      )}

      {/* WhatsApp share — Growth idea: invia direttamente al cliente. */}
      <button
        onClick={onWhatsAppShare}
        disabled={busy || (isCarousel ? carouselSlides.length === 0 : !isStatic && !previewUrl)}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#25D366] hover:bg-[#1FB458] text-white text-[13px] font-semibold transition-colors disabled:opacity-50 shadow-sm"
        data-testid="social-whatsapp-btn"
        title={
          isVideoFormat
            ? "Crea un link condivisibile (7 giorni) e apri WhatsApp con il messaggio già scritto"
            : "Scarica l'immagine + apri WhatsApp (su mobile: condivisione diretta con allegato)"
        }
      >
        <WhatsappLogo size={16} weight="fill" />
        {isVideoFormat ? "Invia al cliente su WhatsApp" : "Apri WhatsApp con l'immagine"}
      </button>

      {isStatic && (
        <div className="mt-3 text-[10.5px] text-[#9A968B] italic text-center leading-relaxed bg-[#F1F4ED]/60 border border-[#E5E7E0] rounded-md py-2 px-3">
          {active === "brochure"
            ? "PDF A4 · 4 pagine · 424 DPI per stampa di qualità · 8-12 secondi"
            : active === "brochure_social"
            ? "4 slide PNG 1080×1350 (4:5 Instagram) · estrai lo .zip sul telefono e carica le 4 slide come carosello."
            : active === "open_house" || active === "story_nuovo" || active === "story_venduto"
            ? "PNG 1080×1920 · pronto per Story IG e WhatsApp."
            : "PNG 1080×1080 · pronto per Instagram feed e Facebook."}
        </div>
      )}

      {/* Per carosello: anche download della singola slide visibile */}
      {isCarousel && carouselSlides.length > 0 && (
        <button
          onClick={() => onDownloadSlide(carouselSlides[carouselIdx])}
          className="mt-3 w-full text-[12px] text-[#6B6A65] hover:text-[#1C1C1A] underline underline-offset-2 transition-colors"
          data-testid="carousel-download-single"
        >
          Scarica solo questa slide ({carouselIdx + 1})
        </button>
      )}

      {/* iter v28r12 — Carosello scaricabile anche come Reel video 9:16.
          Use case: l'agente vuole pubblicare il carosello come Reel
          (5x più reach su Instagram). Endpoint backend renderizza le 5
          slide PNG con cross-fade + bg blur Instagram-style. */}
      {isCarousel && (
        <button
          onClick={() => onDownloadAsReel && onDownloadAsReel()}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[#1C1C1A] hover:bg-[#2A2A26] text-white text-[12.5px] font-semibold transition-colors"
          data-testid="carousel-download-as-reel"
          title="Esporta come video MP4 9:16 con cross-fade tra le 5 slide. Pronto per Instagram Reels e TikTok."
        >
          Scarica come Reel (video 9:16)
        </button>
      )}

      {/* iter 313 r21 v12 — RIMOSSO "Crea link condivisibile (7 giorni)" su
          richiesta utente. Per i reel: l'agente scarica e condivide via Web
          Share API direttamente (Salva in Foto → IG/WhatsApp). Niente più
          link pubblici a TTL che possono leakare la proprietà. */}

      <p className="mt-4 text-[10.5px] text-[#9A968B] italic text-center leading-relaxed">
        {active === "reel"
          ? "Video 1080×1920 · 6 secondi · MP4 leggero. Funziona su Reel, Story, TikTok, WhatsApp."
          : active === "before_after"
          ? "Video 1080×1920 · 5 secondi · wipe trasformazione. Mostra l'effetto Pronto su una tua foto."
          : active === "post"
          ? "PNG 1080×1080 · pronta per Instagram feed e Facebook."
          : active === "carousel"
          ? "5 slide PNG 1080×1080. Estrai lo .zip sul telefono e carica le 5 slide insieme su Instagram come carosello."
          : active === "brochure"
          ? ""
          : active === "brochure_social"
          ? ""
          : active === "open_house" || active === "story_nuovo" || active === "story_venduto" || active === "story"
          ? "PNG 1080×1920 · pronta per WhatsApp Status e Instagram Story."
          : ""}
      </p>

      {/* Caption AI · iter v28 r146 — User M1063: spostato dal pannello dx
          (sotto anteprima) al pannello sx (config), così "Testo del post"
          sta a sinistra e "Pacchetto social completo" a destra (inversione
          richiesta esplicitamente). Lo renderizziamo solo se richiesto via
          prop, per evitare doppia istanza nei layout legacy. */}
      {!isStatic && showCaption && (
        <CaptionGenerator
          propertyId={propertyId}
          occasion={captionOccasion}
          eventDate={captionEventDate}
          eventTime={captionEventTime}
          eventTimeEnd={captionEventTimeEnd}
          daysToSell={captionDaysToSell}
          tone={captionTone}
          setTone={setCaptionTone}
        />
      )}
    </>
  );
}
